import type { Locale } from '../locale';

export const incidentAgentInstruction = 'Inspect one event to confirm its shape. Run the aggregation over all service files inside the workspace and return only the decision summary. Write the complete requested JSON and check its shape locally.';

const en = {
  title: 'Incident reconciliation trial',
  intro: 'Ask your agent to reconcile an incident report. Review the run together and try one change if the activity shows an opportunity.',
  open: 'Open the incident trial',
  transfer: 'Try the next incident',
  transferIntro: 'Apply the workflow you learned to a different incident window. Write the brief in your own words, then check the agent’s report.',
  hosted: 'Stop lending your browser tab before opening the incident trial. Your agent needs to use the hosted workspace.',
  lesson: 'Summarize inside the workspace',
  observed: (services: string, bytes: string) => `Your agent received complete source records for ${services} services in tool output. Those source records accounted for ${bytes} bytes.`,
  instruction: `Add this to your usual brief. “${incidentAgentInstruction}”`,
  noOpportunity: 'Your baseline did not return enough complete source files for this lesson to suggest a change. You can keep your workflow and continue.',
  unavailable: 'There is no usable tool output record for this baseline. You can keep your result and continue.',
  keep: 'Keep my baseline and continue',
  cheaper: 'Both runs passed with the same model. On the next incident, try keeping the full dataset in the workspace and returning the decision summary.',
  course: 'Practice this in the full course',
};

export const incidentTrialCopy: Record<Locale, typeof en> = {
  en,
  ar: {
    title: 'تجربة مراجعة سجلات الحوادث',
    intro: 'اطلب من وكيلك مراجعة تقرير حادث. راجع المحاولة معه وجرّب تغييرًا واحدًا إذا كشف سجل النشاط عن فرصة للتحسين.',
    open: 'افتح تجربة الحادث',
    transfer: 'جرّب الحادث التالي',
    transferIntro: 'طبّق الطريقة التي تعلّمتها على فترة حادث مختلفة. اكتب التكليف بأسلوبك ثم افحص تقرير الوكيل.',
    hosted: 'أوقف مشاركة تبويب المتصفح قبل فتح تجربة الحادث. يحتاج الوكيل إلى استخدام مساحة العمل المستضافة.',
    lesson: 'لخّص داخل مساحة العمل',
    observed: (services, bytes) => `تلقّى وكيلك سجلات المصدر الكاملة لـ ${services} من الخدمات ضمن مخرجات الأدوات. بلغ حجم هذه السجلات ${bytes} بايت.`,
    instruction: 'أضف إلى تكليفك المعتاد «افحص حدثًا واحدًا للتعرّف على بنيته. اجمع البيانات من جميع ملفات الخدمات داخل مساحة العمل وأعد ملخص القرارات فقط. اكتب ملف JSON المطلوب كاملًا وتحقّق من بنيته داخل مساحة العمل.»',
    noOpportunity: 'لم تُرجع محاولتك الأساسية عددًا كافيًا من ملفات المصدر الكاملة ليقترح هذا الدرس تغييرًا. يمكنك المتابعة بطريقتك الحالية.',
    unavailable: 'لا يتوفر سجل صالح لمخرجات الأدوات في هذه المحاولة الأساسية. يمكنك الاحتفاظ بنتيجتك والمتابعة.',
    keep: 'احتفظ بمحاولتي الأساسية وتابع',
    cheaper: 'نجحت المحاولتان بالنموذج نفسه. في الحادث التالي، جرّب إبقاء البيانات الكاملة في مساحة العمل وإرجاع ملخص القرارات.',
    course: 'تدرّب على ذلك في الدورة الكاملة',
  },
  de: {
    title: 'Probelauf zur Vorfallauswertung',
    intro: 'Lass deinen Agenten einen Vorfall auswerten. Prüfe den Lauf und ändere einen Schritt, wenn das Aktivitätsprotokoll eine Gelegenheit dazu zeigt.',
    open: 'Vorfall-Probelauf öffnen',
    transfer: 'Nächsten Vorfall bearbeiten',
    transferIntro: 'Wende deine Arbeitsweise auf ein anderes Vorfallfenster an. Formuliere den Auftrag selbst und prüfe den Bericht deines Agenten.',
    hosted: 'Beende die Freigabe deines Browser-Tabs, bevor du den Probelauf öffnest. Dein Agent braucht den Arbeitsbereich auf dem Server.',
    lesson: 'Im Arbeitsbereich zusammenfassen',
    observed: (services, bytes) => `Dein Agent erhielt vollständige Quelldaten für ${services} Dienste als Werkzeugausgabe. Diese Quelldaten umfassten ${bytes} Byte.`,
    instruction: 'Ergänze deinen üblichen Auftrag. „Prüfe anhand eines Ereignisses das Datenformat. Werte alle Dienstdateien im Arbeitsbereich aus und gib nur die Zusammenfassung der Entscheidungen zurück. Schreibe die vollständige geforderte JSON-Datei und prüfe ihre Struktur vor Ort.“',
    noOpportunity: 'Dein Ausgangslauf gab zu wenige vollständige Quelldateien zurück, um aus dieser Lektion eine Änderung abzuleiten. Du kannst mit deiner bisherigen Arbeitsweise fortfahren.',
    unavailable: 'Für diesen Ausgangslauf liegt kein nutzbares Protokoll der Werkzeugausgaben vor. Du kannst dein Ergebnis behalten und fortfahren.',
    keep: 'Ausgangslauf behalten und fortfahren',
    cheaper: 'Beide Läufe haben mit demselben Modell bestanden. Lass beim nächsten Vorfall den vollständigen Datensatz im Arbeitsbereich und gib die Zusammenfassung der Entscheidungen zurück.',
    course: 'Im vollständigen Kurs weiterüben',
  },
  ja: {
    title: 'インシデント集計の体験課題',
    intro: 'エージェントにインシデント報告の集計を依頼します。実行を振り返り、作業記録に改善の余地が見つかったら手順を一つ変えてみましょう。',
    open: 'インシデント課題を開く',
    transfer: '次のインシデントに挑戦する',
    transferIntro: '学んだ手順を別の時間帯のインシデントに適用します。自分の言葉で依頼を書き、エージェントの報告を確認してください。',
    hosted: 'インシデント課題を開く前に、ブラウザータブの共有を停止してください。エージェントはサーバー上の作業環境を使用します。',
    lesson: '作業環境内で集計する',
    observed: (services, bytes) => `エージェントはツール出力で ${services} サービス分の完全な元データを受け取りました。元データの量は ${bytes} バイトでした。`,
    instruction: '普段の依頼に次を加えてください。「イベントを一件確認して構造を把握する。作業環境内ですべてのサービスファイルを集計し、判断結果の要約だけを返す。指定された JSON 全体を書き出し、その構造を作業環境内で確認する。」',
    noOpportunity: '基準実行で返された完全な元ファイルが少なかったため、このレッスンから提案できる変更はありません。今の手順のまま進めます。',
    unavailable: 'この基準実行には利用できるツール出力の記録がありません。結果を保持したまま続けられます。',
    keep: '基準実行を保存したまま進む',
    cheaper: 'どちらの実行も同じモデルで合格しました。次のインシデントでも、データ全体は作業環境に置き、判断結果の要約を返してみましょう。',
    course: '全コースでこの手順を練習する',
  },
};
